const PKCE_VERIFIER_KEY = 'elva_pkce_verifier';
const PKCE_STATE_KEY = 'elva_pkce_state';
const OAUTH_REDIRECT_URI_KEY = 'elva_oauth_redirect_uri';

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBase64Url(byteLength: number): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));
}

/** 32 random bytes → a 43-char verifier; its S256 challenge is always exactly 43 chars. */
export async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomBase64Url(32);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: toBase64Url(new Uint8Array(digest)) };
}

export function createOAuthState(): string {
  return randomBase64Url(16);
}

/**
 * sessionStorage only — verifier, state and redirect_uri must survive the redirect and
 * match exactly on redeem.
 */
export function storePkceSession(verifier: string, state: string, redirectUri: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  sessionStorage.setItem(OAUTH_REDIRECT_URI_KEY, redirectUri);
}

export function readPkceVerifier(): string | null {
  return sessionStorage.getItem(PKCE_VERIFIER_KEY);
}

export function readPkceState(): string | null {
  return sessionStorage.getItem(PKCE_STATE_KEY);
}

/** Same `redirect_uri` sent on `/login` — required again on `/auth/handoff/redeem`. */
export function readOAuthRedirectUri(): string | null {
  return sessionStorage.getItem(OAUTH_REDIRECT_URI_KEY);
}

export function clearPkceSession(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(OAUTH_REDIRECT_URI_KEY);
}

/** `state` never reaches the account API — it is ours to generate and verify. */
export function verifyOAuthState(returnedState: string | null): boolean {
  const expected = readPkceState();
  return !!expected && !!returnedState && expected === returnedState;
}
